type VersionMonitorOptions = {
  currentVersion: string
  publicUrl?: string
  onUpdate: (version: string | null) => void
}

const POLL_INTERVAL = 60_000
const REQUEST_TIMEOUT = 8_000

/** Compare with this tab's bundle, never with a version shared by other tabs. */
export function startVersionMonitor({
  currentVersion,
  publicUrl = '',
  onUpdate,
}: VersionMonitorOptions): () => void {
  if (!currentVersion) return () => {}

  let stopped = false
  let request: AbortController | null = null
  let timeout: ReturnType<typeof setTimeout> | undefined

  const check = async () => {
    if (stopped || request || document.visibilityState === 'hidden' || navigator.onLine === false) {
      return
    }

    const controller = new AbortController()
    request = controller
    timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT)

    try {
      const response = await fetch(`${publicUrl.replace(/\/$/, '')}/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      if (!response.ok) return

      const data = await response.json()
      const version = data?.version
      if (
        stopped ||
        controller.signal.aborted ||
        typeof version !== 'string' ||
        !/^[a-zA-Z0-9][a-zA-Z0-9._:-]{0,199}$/.test(version)
      ) {
        return
      }

      onUpdate(version === currentVersion ? null : version)
    } catch {
      // Offline, deployment in progress or timeout: keep the application usable.
    } finally {
      clearTimeout(timeout)
      request = null
    }
  }

  const interval = setInterval(check, POLL_INTERVAL)
  window.addEventListener('focus', check)
  window.addEventListener('online', check)
  window.addEventListener('pageshow', check)
  document.addEventListener('visibilitychange', check)
  void check()

  return () => {
    stopped = true
    clearInterval(interval)
    clearTimeout(timeout)
    request?.abort()
    window.removeEventListener('focus', check)
    window.removeEventListener('online', check)
    window.removeEventListener('pageshow', check)
    document.removeEventListener('visibilitychange', check)
  }
}

/** One automatic navigation per target version, even if a proxy serves stale HTML. */
export function reloadForVersion(version: string, manual = false): boolean {
  const url = new URL(window.location.href)
  const storageKey = 'app_version_reload'
  const attempt = `${process.env.REACT_APP_BUILD_VERSION || ''}:${version}`

  if (!manual && url.searchParams.get('_v') === version) return false

  try {
    if (!manual && sessionStorage.getItem(storageKey) === attempt) return false
    sessionStorage.setItem(storageKey, attempt)
  } catch {
    // The URL guard still prevents loops when storage is unavailable.
  }

  url.searchParams.set('_v', version)
  if (manual) url.searchParams.set('_refresh', String(Date.now()))
  window.location.replace(url.toString())
  return true
}
